/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { SpacingSize, SPACING_SCALES, uiSizeOrder, STORAGE_KEYS } from '@/constants/config';

interface SpacingContextType {
  spacing: SpacingSize;
  setSpacing: (size: SpacingSize) => void;
  cycleSpacing: () => void;
  scale: number;
}

const SpacingContext = createContext<SpacingContextType | undefined>(undefined);

export function SpacingProvider({ children }: { children: ReactNode }) {
  const [spacing, setSpacingState] = useState<SpacingSize>(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.spacing);
    if (stored && (stored === 'compact' || stored === 'standard' || stored === 'comfortable')) {
      return stored;
    }
    return 'standard';
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.spacing, spacing);

    // SET CSS VARIABLE - Tailwind spacing will multiply by this
    const scale = SPACING_SCALES[spacing];
    document.documentElement.style.setProperty('--spacing-scale', String(scale));

    // Also set pre-calculated common spacing values for direct use
    document.documentElement.style.setProperty('--space-1', `${4 * scale}px`);
    document.documentElement.style.setProperty('--space-2', `${8 * scale}px`);
    document.documentElement.style.setProperty('--space-3', `${12 * scale}px`);
    document.documentElement.style.setProperty('--space-4', `${16 * scale}px`);
    document.documentElement.style.setProperty('--space-5', `${20 * scale}px`);
    document.documentElement.style.setProperty('--space-6', `${24 * scale}px`);
    document.documentElement.style.setProperty('--space-8', `${32 * scale}px`);
    document.documentElement.style.setProperty('--space-10', `${40 * scale}px`);
    document.documentElement.style.setProperty('--space-12', `${48 * scale}px`);
  }, [spacing]);

  const setSpacing = (size: SpacingSize) => {
    setSpacingState(size);
  };

  const cycleSpacing = () => {
    const currentIndex = uiSizeOrder.indexOf(spacing);
    const nextIndex = (currentIndex + 1) % uiSizeOrder.length;
    setSpacingState(uiSizeOrder[nextIndex]);
  };

  return (
    <SpacingContext.Provider
      value={{
        spacing,
        setSpacing,
        cycleSpacing,
        scale: SPACING_SCALES[spacing],
      }}
    >
      {children}
    </SpacingContext.Provider>
  );
}

export function useSpacing() {
  const context = useContext(SpacingContext);
  if (context === undefined) {
    throw new Error('useSpacing must be used within a SpacingProvider');
  }
  return context;
}
